import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { Box, Button, Typography } from '@mui/material';
import ModalFormWrapper from '@sharedCR/coreDesignSystem/ModalFormWrapper';
import { LOANS_ROUTING_PARAMS, LoansPaths } from 'app/configs/routesConfig/loans';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import { HTTP_RESPONSE_STATUS_CODES } from 'constants/http';
import { LoanApplicationTabRouteParam } from 'constants/loanApplication';
import { getDefaultErrorMessage } from 'utils/axios';

import {
  closeDialogAction,
  hideMessageAction,
  showErrorMessageAction,
  showMessageAction,
} from 'store/global/actions';
import { saveApplicationChangesAction } from 'store/loanApplications/actions';

import { LoanHeadersTestIds } from '../LoanHeaders.testIds';
import { getDisclosureBlockedMessage } from '../LoanHeaders.utils';

import {
  SendDisclosureModalActions,
  sendDisclosureModalActionToDelayHours,
  sendDisclosureModalActionToSuccessMessage,
  SendDisclosureType,
} from './LoanInfoHeader.constants';
import { SendDisclosuresFormTestIds } from './SendDisclosuresForm.testIds';

const creditMessageId = 'creditReportMessage434';
const creditMessageDuration = 180000;

const SendDisclosuresForm = ({ setIsLoading, isLoading, formRef, disclosureType, title }) => {
  const dispatch = useDispatch();

  const { applicationId } = useParams();
  const history = useHistory();

  const isInitialDisclosure = disclosureType === SendDisclosureType.INITIAL;

  const onNavigateToOrderCredit = useCallback(() => {
    dispatch(hideMessageAction(creditMessageId));

    return LoansPaths.applicationManagement.navigateWith(history.push, {
      [LOANS_ROUTING_PARAMS.applicationId]: applicationId,
      [LOANS_ROUTING_PARAMS.tab]: LoanApplicationTabRouteParam.BORROWERS,
    });
  }, [dispatch, applicationId, history.push]);

  const noCreditErrorMessage = useMemo(
    () => (
      <div>
        <p>
          Credit report must be obtained prior to locking a loan. The credit report can be ordered
          on the Personal Info/Credit tab.
        </p>
        <Button
          className="mt-8"
          data-testid={SendDisclosuresFormTestIds.ORDER_CREDIT_BUTTON}
          variant="outlined"
          onClick={onNavigateToOrderCredit}
        >
          Order Credit
        </Button>
      </div>
    ),
    [onNavigateToOrderCredit],
  );

  const onClose = useCallback(() => dispatch(closeDialogAction()), [dispatch]);

  const onSubmit = useCallback(
    e => {
      e.preventDefault();

      setIsLoading(true);

      const dataType = e.currentTarget.dataset.type || SendDisclosureModalActions.AGREE;

      const actionData = {
        applicationId,
        loanCompletionDelayInHours: sendDisclosureModalActionToDelayHours[dataType],
        silent: true,
      };

      const message = sendDisclosureModalActionToSuccessMessage[dataType];

      dispatch(saveApplicationChangesAction(actionData))
        .then(result => {
          dispatch(closeDialogAction());

          const blockedMessage = getDisclosureBlockedMessage(result?.payload?.metadata);

          if (blockedMessage) {
            dispatch(showMessageAction({ message: blockedMessage, variant: 'error' }));

            return;
          }

          dispatch(showMessageAction({ message, variant: 'success' }));
        })
        .catch(err => {
          if (err.response.status === HTTP_RESPONSE_STATUS_CODES.NO_CREDIT_REPORT) {
            dispatch(
              showMessageAction({
                variant: 'error',
                message: noCreditErrorMessage,
                duration: creditMessageDuration,
                id: creditMessageId,
              }),
            );

            dispatch(closeDialogAction());

            return;
          }

          dispatch(
            showErrorMessageAction({
              message: getDefaultErrorMessage(err),
              err,
            }),
          );
        })
        .finally(() => setIsLoading(false));
    },
    [setIsLoading, applicationId, dispatch, noCreditErrorMessage],
  );

  return (
    <ModalFormWrapper
      isWithCustomButtons
      formRef={formRef}
      formTestId="send-disclosures-form"
      isSubmitting={isLoading}
      onSubmit={onSubmit}
    >
      <Typography variant="regular14">{title}</Typography>

      <Box className="flex self-end gap-16">
        <Button
          data-testid={LoanHeadersTestIds.ASSOCIATE_REPORT_FORM_LOAN_HEADER_CANCEL_BUTTON}
          data-type={SendDisclosureModalActions.REJECT}
          disabled={isLoading}
          type="button"
          variant="line"
          onClick={isInitialDisclosure ? onSubmit : onClose}
        >
          No
        </Button>

        <Button
          data-testid={LoanHeadersTestIds.ASSOCIATE_REPORT_FORM_LOAN_HEADER_SUBMIT_BUTTON}
          data-type={SendDisclosureModalActions.AGREE}
          disabled={isLoading}
          type="submit"
          variant="contained"
        >
          Yes
        </Button>
      </Box>
    </ModalFormWrapper>
  );
};

SendDisclosuresForm.propTypes = {
  ...WithLoadingPropTypes,
  disclosureType: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};

export default compose(withLoading, memo)(SendDisclosuresForm);
